import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';

import { ENTRA_AUTH_CONFIG } from './entra-auth.config';
import { EntraAuthService } from './entra-auth.service';
import { entraAuthGuard } from './entra-auth.guard';

/**
 * Guards routes that require membership in the "MKZS - Portal Admin" Entra group.
 *
 * First runs the standard entraAuthGuard to ensure the user is authenticated.
 * Then checks the "groups" claim in the ID token for the admin group Object ID.
 * Authenticated users who are not admins are redirected to /account.
 *
 * Do not use this guard for routes that only require authentication — use entraAuthGuard instead.
 */
export const entraAdminGuard: CanActivateFn = async (route, state) => {
  const config = inject(ENTRA_AUTH_CONFIG);
  const authService = inject(EntraAuthService);
  const router = inject(Router);

  // If auth is disabled, bypass — consistent with entraAuthGuard behaviour
  if (!config.enabled) {
    return true;
  }

  // Run the standard auth guard first — if the user is not authenticated,
  // this will redirect to sign-in and return false.
  const isAuthenticated = await entraAuthGuard(route, state);
  if (!isAuthenticated) {
    return false;
  }

  // User is authenticated — check the groups claim in the token
  if (authService.isPortalAdmin()) {
    return true;
  }

  // Authenticated but not in the admin group — redirect to Account
  router.navigate(['/account']);
  return false;
};
